import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { AlertCircle, Briefcase, Loader2, Lock, Mail, Scale, ShieldCheck, User } from "lucide-react";
import { formatApiError, homeFor, useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";

const ROLES = [
  { id: "lawyer", label: "Lawyer", icon: Briefcase },
  { id: "staff", label: "Staff", icon: ShieldCheck },
  { id: "client", label: "Client", icon: User },
];
const DEMOS = [
  { role: "lawyer", name: "Adv. Demo Lawyer", email: "lawyer@demo.com" },
  { role: "staff", name: "Demo Staff", email: "staff@demo.com" },
  { role: "client", name: "Demo Client", email: "client@demo.com" },
];

const Field = ({ icon: Icon, ...props }) => (
  <div className="relative">
    <Icon size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
    <input {...props} className="h-12 w-full rounded-xl border border-slate-200 bg-slate-50 pl-11 pr-4 text-sm text-navy outline-none transition-[border-color,box-shadow,background-color] focus:border-amber-400 focus:bg-white focus:ring-4 focus:ring-amber-100" />
  </div>
);

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("lawyer");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to={homeFor(user)} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password, role);
      navigate(homeFor(u), { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const fill = (d) => {
    setEmail(d.email);
    setPassword("Demo@123");
    setRole(d.role);
    setError("");
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-navy px-4 py-10 sm:py-16">
      <div className="pointer-events-none absolute -top-40 left-1/2 h-[480px] w-[480px] -translate-x-1/2 rounded-full bg-amber-500/20 blur-[120px]" />
      <div className="pointer-events-none absolute inset-0 opacity-[0.06] bg-grid" />
      <div className="relative mx-auto w-full max-w-md animate-fade-up">
        <div data-testid="login-card" className="rounded-3xl bg-white p-6 shadow-2xl sm:p-8">
          <div className="flex flex-col items-center text-center">
            <div className="grid h-14 w-14 place-items-center rounded-2xl bg-navy text-gold shadow-lg"><Scale size={28} /></div>
            <h1 className="mt-5 font-heading text-3xl font-extrabold tracking-tight text-navy">My Lawyer <span className="text-gold">My Client</span></h1>
            <p className="mt-2 text-sm text-slate-500">One secure place for you and your lawyer.</p>
          </div>
          <form onSubmit={submit} className="mt-8 flex flex-col gap-4">
            <Field icon={Mail} data-testid="login-email-input" type="email" placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} required />
            <Field icon={Lock} data-testid="login-password-input" type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            <div>
              <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">I am a</div>
              <div className="relative grid grid-cols-3 rounded-xl bg-slate-100 p-1">
                <span className="absolute inset-y-1 left-1 w-[calc((100%-0.5rem)/3)] rounded-lg bg-white shadow-sm transition-transform duration-300 ease-out"
                  style={{ transform: `translateX(${ROLES.findIndex((r) => r.id === role) * 100}%)` }} />
                {ROLES.map(({ id, label, icon: Icon }) => (
                  <button key={id} type="button" data-testid={`role-select-${id}`} onClick={() => setRole(id)}
                    className={cn("relative z-10 flex h-11 items-center justify-center gap-1.5 text-sm font-semibold transition-colors", role === id ? "text-navy" : "text-slate-500")}>
                    <Icon size={15} className={role === id ? "text-gold" : ""} />{label}
                  </button>
                ))}
              </div>
            </div>
            {error && (
              <div data-testid="login-error" className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                <AlertCircle size={16} />{error}
              </div>
            )}
            <button data-testid="login-submit-button" type="submit" disabled={loading}
              className="mt-1 flex h-12 items-center justify-center gap-2 rounded-xl bg-navy font-semibold text-white transition-[background-color,transform] hover:bg-navy-light active:scale-[0.98] disabled:opacity-70">
              {loading && <Loader2 size={18} className="animate-spin" />}Login
            </button>
          </form>
        </div>
        <div data-testid="demo-credentials-box" className="mt-5 rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur-xl">
          <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-amber-400">Demo credentials · tap to autofill</div>
          <div className="flex flex-col gap-2">
            {DEMOS.map((d) => (
              <button key={d.role} type="button" data-testid={`demo-autofill-${d.role}`} onClick={() => fill(d)}
                className="flex min-h-[48px] items-center justify-between rounded-xl bg-white/5 px-4 text-left transition-colors hover:bg-white/10">
                <div>
                  <div className="text-sm font-semibold text-white">{d.name}</div>
                  <div className="text-xs text-slate-400">{d.email} · Demo@123</div>
                </div>
                <span className="rounded-full bg-amber-500/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-amber-400">{d.role}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
